/**
 * Activity log → GA4. The activity log (features/activityLog) already sees
 * every playback, search and lesson; its emit() hands each event it keeps to
 * the listener installed here, AFTER its own rules (off, impersonation,
 * server suspension) have run, so GA4 never gets more than the log does.
 *
 * Only a short whitelist is mapped (README.md lists it); everything else in
 * the log stays in our own log only. Free text is limited to the SUP search
 * term, cleaned of emails and digit runs (cleanText) and cut to 100 chars;
 * no email, name or user id is ever passed on.
 */
import { cleanText } from "@/features/activityLog/lib/sanitize";
import type { EventParams } from "./dataLayer";
import { coursePartOf, mediaDurationHint } from "./learning";
import { analyticsChapterAt, analyticsContext } from "./mediaContext";
import { currentPageParams } from "./pageView";

/** The part of an activity-log event the bridge reads. */
export interface BridgeEvent {
    type: string;
    ctx?: Record<string, unknown>;
    data?: Record<string, unknown>;
}

export interface GaEvent {
    event: string;
    params: EventParams;
}

export const PROGRESS_BUCKETS = [10, 25, 50, 75, 90] as const;
const MAX_TRACKED_PLAYBACKS = 50;

type Obj = Record<string, unknown>;

const obj = (v: unknown): Obj | undefined =>
    v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : undefined;
const str = (v: unknown, max = 100): string | undefined =>
    typeof v === "string" && v.trim() ? v.trim().slice(0, max) : typeof v === "number" ? String(v) : undefined;
const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

interface PlaybackState {
    media: EventParams;
    /** The player named the media itself (lecture title): the page's lesson title does not replace it. */
    ownTitle: boolean;
    /** page_location / page_route at play_start: progress and stop keep them, not the next page's. */
    page: EventParams;
    /** Path of the page at play_start: the page's context names this playback only while that page is showing. */
    path?: string;
    duration?: number;
    fired: Set<number>;
}

const mediaParams = (data: Obj, ev: BridgeEvent): EventParams => {
    const media = obj(data.media) || {};
    // Course / lesson: the page's analytics-only context (mediaContext.ts),
    // else whatever the log's own ctx already carries.
    const ctx = { ...analyticsContext(), ...(ev.ctx || {}), ...(obj(data.ctx) || {}) };
    return {
        video_title: str(media.title) ?? str(ctx.video_title),
        media_kind: str(media.kind, 40),
        media_id: str(media.id),
        course_id: str(ctx.course_id),
        course_part: coursePartOf(ctx.course_id),
        lesson_id: str(ctx.lesson_id),
    };
};

/**
 * A playback GA4 could not name (no kind, id, title or course) — e.g. a muted
 * autoplaying hero clip behind hls.js — is not reported: it says nothing, and
 * an autoplay is no viewer action.
 */
const isNamed = (m: EventParams): boolean => !!(m.media_kind || m.media_id || m.video_title || m.course_id);

/** Duration an iframe player (Kinescope) reported outside the log (learning.ts). */
const currentPath = (): string | undefined => {
    try {
        return window.location.pathname;
    } catch {
        return undefined;
    }
};

const hintedDuration = (media: EventParams): number | undefined =>
    mediaDurationHint(media.media_kind, media.media_id);

/**
 * The lesson the playback is in NOW. A single-video course has one player for
 * every chapter, so the lesson at play_start goes stale: the chapter at `posS`
 * (chapters registered by the course view) wins, else the page's current
 * analytics context for the same course while the page the playback started
 * on is still showing, else what the playback last had (the page may be gone
 * by play_end, or be the next lesson of the same course: the lesson page is
 * one component for every lesson, and the log ends the old playback after the
 * route already set the new lesson). Updates `state.media` in place.
 */
const refreshLesson = (state: PlaybackState, posS: number | undefined): void => {
    const courseId = state.media.course_id;
    if (typeof courseId !== "string") return;
    let lessonId: string | undefined;
    let title: string | undefined;
    const chapter = analyticsChapterAt(courseId, posS);
    if (chapter) {
        lessonId = str(chapter.lesson_id);
        title = str(chapter.title);
    } else {
        if (!state.path || currentPath() !== state.path) return;
        const ctx = analyticsContext();
        if (ctx.course_id !== courseId) return;
        lessonId = str(ctx.lesson_id);
        title = str(ctx.video_title);
    }
    if (!lessonId) return;
    state.media = {
        ...state.media,
        lesson_id: lessonId,
        video_title: state.ownTitle ? state.media.video_title : title ?? state.media.video_title,
    };
};

const percentOf = (pos: number | undefined, duration: number | undefined): number | undefined => {
    if (pos === undefined || !duration || duration <= 0) return undefined;
    return Math.max(0, Math.min(100, Math.round((pos / duration) * 100)));
};

/**
 * Maps activity-log events to GA4 events. Keeps per-playback state (duration,
 * progress buckets already sent), so one instance per page.
 */
export class ActivityBridge {
    private readonly playbacks = new Map<string, PlaybackState>();
    private readonly unnamed = new Set<string>();

    map(ev: BridgeEvent): GaEvent[] {
        const data = obj(ev.data) || {};
        switch (ev.type) {
            case "play_start":
                return this.playStart(ev, data);
            case "play_progress":
                return this.playProgress(data);
            case "play_end":
                return this.playEnd(ev, data);
            case "search_submit": {
                const term = cleanText(data.query, 100);
                if (!term) return [];
                return [
                    {
                        event: "search",
                        params: {
                            search_term: term,
                            search_trigger: str(data.trigger, 40),
                            query_source: str(data.query_source, 40),
                        },
                    },
                ];
            }
            case "course_lesson_open":
                return [
                    {
                        event: "lesson_open",
                        params: {
                            course_id: str(data.course_id),
                            course_part: coursePartOf(data.course_id),
                            lesson_id: str(data.lesson_id),
                            lesson_kind: str(data.kind, 40),
                        },
                    },
                ];
            case "lecture_open":
                return [
                    {
                        event: "lecture_open",
                        params: {
                            media_id: str(data.media_id),
                            video_title: str(data.title),
                            lecture_source: str(data.source, 40),
                            stream_live: typeof data.stream_live === "boolean" ? data.stream_live : undefined,
                        },
                    },
                ];
            case "auth_login": {
                // "other_tab" is a sign-in noticed from another tab, not a new one.
                const method = str(data.method, 40);
                if (!method || method === "other_tab") return [];
                return [{ event: "login", params: { method } }];
            }
            default:
                return [];
        }
    }

    private remember(id: string, state: PlaybackState): void {
        if (this.playbacks.size >= MAX_TRACKED_PLAYBACKS) {
            const oldest = this.playbacks.keys().next().value;
            if (oldest !== undefined) this.playbacks.delete(oldest);
        }
        this.playbacks.set(id, state);
    }

    private playStart(ev: BridgeEvent, data: Obj): GaEvent[] {
        const media = mediaParams(data, ev);
        const duration = num(data.duration_s) ?? hintedDuration(media);
        const id = str(data.playback_id, 80);
        if (!isNamed(media)) {
            if (id) {
                if (this.unnamed.size >= MAX_TRACKED_PLAYBACKS) this.unnamed.clear();
                this.unnamed.add(id);
            }
            return [];
        }
        const state: PlaybackState = {
            media,
            ownTitle: !!str(obj(data.media)?.title),
            page: currentPageParams(),
            path: currentPath(),
            duration,
            fired: new Set(),
        };
        refreshLesson(state, num(data.start_s));
        if (id) this.remember(id, state);
        return [
            {
                event: "video_start",
                params: {
                    ...state.page,
                    ...state.media,
                    video_duration: duration !== undefined ? Math.round(duration) : undefined,
                    video_current_time: num(data.start_s) !== undefined ? Math.round(num(data.start_s)!) : undefined,
                },
            },
        ];
    }

    /** video_progress for every bucket newly reached by the furthest position. */
    private buckets(state: PlaybackState, maxS: number | undefined): GaEvent[] {
        const pct = percentOf(maxS, state.duration);
        if (pct === undefined) return [];
        const out: GaEvent[] = [];
        for (const b of PROGRESS_BUCKETS) {
            if (pct >= b && !state.fired.has(b)) {
                state.fired.add(b);
                out.push({
                    event: "video_progress",
                    params: {
                        ...state.page,
                        ...state.media,
                        video_percent: b,
                        video_duration: Math.round(state.duration!),
                    },
                });
            }
        }
        return out;
    }

    private playProgress(data: Obj): GaEvent[] {
        const id = str(data.playback_id, 80);
        const state = id ? this.playbacks.get(id) : undefined;
        if (!state) return [];
        if (state.duration === undefined) state.duration = hintedDuration(state.media);
        refreshLesson(state, num(data.pos_s) ?? num(data.max_s));
        return this.buckets(state, num(data.max_s));
    }

    private playEnd(ev: BridgeEvent, data: Obj): GaEvent[] {
        const id = str(data.playback_id, 80);
        const known = id ? this.playbacks.get(id) : undefined;
        if (id && this.unnamed.delete(id)) return [];
        const state: PlaybackState = known || {
            media: mediaParams(data, ev),
            ownTitle: !!str(obj(data.media)?.title),
            page: {},
            fired: new Set(),
        };
        if (!known && !isNamed(state.media)) return [];
        const duration = num(data.duration_s);
        if (duration !== undefined && duration > 0) state.duration = duration;
        else if (state.duration === undefined) state.duration = hintedDuration(state.media);
        if (id) this.playbacks.delete(id);
        const maxS = num(data.max_s);
        const endS = num(data.end_s);
        // Buckets reached before the stop, then the stop itself: the chapter
        // at the furthest position for the first, at the end position for the second.
        refreshLesson(state, maxS ?? endS);
        const out = this.buckets(state, maxS);
        refreshLesson(state, endS);
        const ended = data.end_reason === "ended";
        const watched = num(data.watched_s);
        out.push({
            event: ended ? "video_complete" : "video_stop",
            params: {
                ...state.page,
                ...state.media,
                video_duration: state.duration !== undefined ? Math.round(state.duration) : undefined,
                video_current_time: endS !== undefined ? Math.round(endS) : undefined,
                video_percent: ended ? 100 : percentOf(maxS, state.duration),
                watched_s: watched !== undefined ? Math.round(watched) : undefined,
                end_reason: str(data.end_reason, 40),
            },
        });
        return out;
    }
}
